/**
 * [INPUT]: 依赖 React 子节点分层/尺寸 memo、cartesian controller、CommonContext 与家族 Canvas
 * [OUTPUT]: 对外提供 CartesianRoot，组装背景 SVG、dither Canvas、双轴尺度下界、前景 SVG、DOM chrome 与 a11y 描述
 * [POS]: Area/Line/Bar/Combo 的结构根，拥有响应尺寸与 pointer scrub 交互
 * [PROTOCOL]: 变更时更新此头部，然后检查 CLAUDE.md
 */
import { Children, isValidElement, useId, useMemo } from "react";
import { ChartContext, useChartController } from "./chart-context";
import { CommonChartContext } from "./common-context"
import { cn } from "./lib"
import { useChartDimensions } from "./use-chart-dimensions"

const DEFAULT_MARGINS = {
  top: 10,
  right: 12,
  bottom: 22,
  left: 36,
}

/** Which render layer a composed part targets — defaults to the front SVG. */
function layerOf(node) {
  if (!isValidElement(node) || typeof node.type === "string") return "svg"
  return (node.type).chartLayer ?? "svg";
}

/**
 * Shared root for the cartesian dither charts (area, line, bar). Owns the
 * measured size, the shared context, and pointer interaction; every visual is
 * composed as children. Back chrome (grid) sits behind the dither canvas; the
 * canvas paints the fill/line/bars + stars; front chrome (axes, dots) and DOM
 * legend/tooltip layer on top. `chartType` drives the scales/interaction and the
 * `Canvas` prop supplies the family's painter (continuous for area/line, bars for
 * bar) — so each chart ships only its own canvas.
 */
export function CartesianRoot(
  {
    chartType,
    Canvas,
    data,
    config,
    children,
    stackType = "default",
    margins: marginsProp,
    className,
    animate = true,
    animationDuration = 900,
    replayToken = 0,
    interactive = true,
    markerIndex = null,
    hovered = false,
    bloom = "off",
    bloomOnHover = false,
    onHoverChange,
    defaultSelectedDataKey = null,
    onSelectionChange,
    rightAxisMax,
    ariaLabel = "数据图表",
    ariaDescription
  }
) {
  const { ref, size } = useChartDimensions()
  const descriptionId = useId()
  const marginTop = marginsProp?.top ?? DEFAULT_MARGINS.top
  const marginRight = marginsProp?.right ?? DEFAULT_MARGINS.right
  const marginBottom = marginsProp?.bottom ?? DEFAULT_MARGINS.bottom
  const marginLeft = marginsProp?.left ?? DEFAULT_MARGINS.left
  const margins = useMemo(() => ({
    top: marginTop,
    right: marginRight,
    bottom: marginBottom,
    left: marginLeft,
  }), [marginTop, marginRight, marginBottom, marginLeft])
  const CanvasLayer = Canvas

  const ctx = useChartController({
    chartType,
    data,
    config,
    stackType,
    dimensions: size,
    margins,
    animate,
    animationDuration,
    replayToken,
    markerIndex,
    hovered,
    bloom,
    bloomOnHover,
    defaultSelectedDataKey,
    onSelectionChange,
    rightAxisMax,
  })

  const backChildren = []
  const svgChildren = []
  const domChildren = []
  Children.forEach(children, (child) => {
    const layer = layerOf(child)
    if (layer === "back") backChildren.push(child)
    else if (layer === "dom") domChildren.push(child)
    else svgChildren.push(child)
  })

  const onMove = (clientX) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const px = clientX - rect.left - margins.left
    const index = ctx.indexAtX(px)
    ctx.setHoverIndex(index)
    ctx.setCursorX(clientX - rect.left)
    onHoverChange?.(index)
  }

  return (
    <ChartContext value={ctx}>
      <CommonChartContext value={ctx.common}>
        <div
          ref={ref}
          data-slot="dither-chart"
          className={cn("relative h-full w-full", className)}
          onPointerEnter={() => ctx.setMouseInChart(true)}
          onPointerMove={interactive ? (e) => onMove(e.clientX) : undefined}
          onPointerLeave={() => {
            ctx.setMouseInChart(false)
            ctx.setHoverIndex(null)
            onHoverChange?.(null)
          }}>
          {ariaDescription && <span id={descriptionId} className="sr-only">{ariaDescription}</span>}
          {ctx.ready && backChildren.length > 0 && (
            <svg
              width={size.width}
              height={size.height}
              className="absolute inset-0 overflow-visible"
              aria-hidden
              role="presentation">
              <g transform={`translate(${margins.left},${margins.top})`}>
                {backChildren}
              </g>
            </svg>
          )}
          <CanvasLayer />
          {ctx.ready && (
            <svg
              width={size.width}
              height={size.height}
              className="absolute inset-0 overflow-visible"
              role="img"
              aria-label={ariaLabel}
              aria-describedby={ariaDescription ? descriptionId : undefined}>
              <g transform={`translate(${margins.left},${margins.top})`}>
                {svgChildren}
              </g>
            </svg>
          )}
          {domChildren}
        </div>
      </CommonChartContext>
    </ChartContext>
  );
}
